/**
 * Accessible Input Component
 * 
 * Provides an input component with built-in accessibility and validation display.
 */

import React, { InputHTMLAttributes, forwardRef } from 'react'
import { AlertCircle } from 'lucide-react'

interface AccessibleInputProps extends InputHTMLAttributes<HTMLInputElement> {
  /**
   * Label text (required for accessibility)
   */
  label: string
  
  /**
   * Error message to display
   */
  error?: string
  
  /**
   * Helper text to display below input
   */
  helperText?: string
  
  /**
   * Whether field is required
   */
  required?: boolean
  
  /**
   * Icon to display before input
   */
  icon?: React.ReactNode
  
  /**
   * Full width input
   */
  fullWidth?: boolean
}

/**
 * AccessibleInput - Input with built-in accessibility and validation
 * 
 * @example
 * ```tsx
 * <AccessibleInput
 *   label="Email Address"
 *   type="email"
 *   value={email}
 *   onChange={(e) => setEmail(e.target.value)}
 *   error={emailError}
 *   required
 * />
 * ```
 */
const AccessibleInput = forwardRef<HTMLInputElement, AccessibleInputProps>(
  ({
    label,
    error,
    helperText,
    required,
    icon,
    fullWidth = true,
    className = '',
    id,
    ...props
  }, ref) => {
    const inputId = id || `input-${label.toLowerCase().replace(/\s+/g, '-')}`
    const errorId = error ? `${inputId}-error` : undefined
    const helperId = helperText ? `${inputId}-helper` : undefined
    
    return (
      <div className={fullWidth ? 'w-full' : ''}>
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          {label}
          {required && (
            <span className="text-red-500 ml-1" aria-label="required">
              *
            </span>
          )}
        </label>
        
        <div className="relative">
          {icon && (
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" aria-hidden="true">
              {icon}
            </div>
          )}
          
          <input
            ref={ref}
            id={inputId}
            className={`
              ${fullWidth ? 'w-full' : ''}
              ${icon ? 'pl-10' : 'pl-4'}
              pr-4 py-2.5
              border rounded-lg
              bg-white dark:bg-gray-800
              text-gray-900 dark:text-white
              placeholder-gray-400 dark:placeholder-gray-500
              focus:outline-none focus:ring-2 focus:ring-offset-2
              transition-colors
              ${error
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 dark:border-gray-600 focus:ring-salt-gold focus:border-salt-gold'
              }
              ${className}
            `}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? errorId : helperId}
            aria-required={required}
            {...props}
          />
        </div>
        
        {error && (
          <div
            id={errorId}
            className="mt-2 flex items-center gap-2 text-sm text-red-600 dark:text-red-400"
            role="alert"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}
        
        {helperText && !error && (
          <div
            id={helperId}
            className="mt-2 text-sm text-gray-500 dark:text-gray-400"
          >
            {helperText}
          </div>
        )}
      </div>
    )
  }
)

AccessibleInput.displayName = 'AccessibleInput'

export default AccessibleInput

